import React, {
  useState,
  useEffect,
  useRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  MouseEventHandler,
} from "react";
import { View } from "@tarojs/components";
import { useTouch } from "../../hooks/useTouch";
import "./index.scss";

interface PickerObjectValue {
  text: string | number;
  value: string | number;
  [key: string]: any;
}
export type PickerValue = string | PickerObjectValue;

interface PickerSlotProps<T extends PickerValue = PickerObjectValue> {
  defaultValue?: string | number;
  data: T[];
  mode?: "normal" | "3D";
  swipeDuration?: number | string;
  onConfirm?: (value: T, index: number) => void;
  onCancel?: MouseEventHandler;
  onClose?: MouseEventHandler;
  keyIndex: number;
  visible: boolean;
}

const InternalPickerSlot: ForwardRefRenderFunction<
  { stopMomentum: () => void; moving: boolean },
  Partial<PickerSlotProps>
> = (props, ref) => {
  const {
    keyIndex = 0,
    defaultValue,
    data = [],
    mode = "normal",
    swipeDuration = 1000,
    visible = false,
    onConfirm,
  } = props;

  const touch = useTouch();

  const DEFAULT_DURATION = 200;
  // 触发惯性滑动条件:
  // 在手指离开屏幕时，如果和上一次 move 时的间隔小于 `MOMENTUM_TIME` 且 move
  // 距离大于 `MOMENTUM_DISTANCE` 时，执行惯性滑动
  const INERTIA_TIME = 300;
  const INERTIA_DISTANCE = 15;
  const [currIndex, setCurrIndex] = useState(1);
  const lineSpacing = useRef(48);

  const [touchTime, setTouchTime] = useState(0);
  const [touchDeg, setTouchDeg] = useState("0deg");
  const rotation = 22;
  const moving = useRef(false);
  let timer: number | undefined;

  const listbox = useRef<any>(null);
  const rollerRef = useRef<any>(null);
  const pickerSlotRef = useRef<any>(null);

  const [startTime, setStartTime] = useState(0);
  const [startY, setStartY] = useState(0);

  const transformY = useRef(0);
  const [scrollDistance, setScrollDistance] = useState(0);

  const isHidden = (index: number) => {
    if (index >= currIndex + 8 || index <= currIndex - 8) {
      return true;
    }
    return false;
  };

  const setTransform = (
    translateY = 0,
    type: string,
    time = DEFAULT_DURATION,
    deg: string
  ) => {
    let nTime = time;
    if (type !== "end") {
      nTime = 0;
    }

    setTouchTime(nTime);
    setTouchDeg(deg);

    setScrollDistance(translateY);
  };

  const setMove = (move: number, type?: string, time?: number) => {
    let updateMove = move + transformY.current;
    if (type === "end") {
      // 限定滚动距离
      if (updateMove > 0) {
        updateMove = 0;
      }
      if (updateMove < -(data.length - 1) * lineSpacing.current) {
        updateMove = -(data.length - 1) * lineSpacing.current;
      }

      // 设置滚动距离为lineSpacing的倍数值
      const endMove =
        Math.round(updateMove / lineSpacing.current) * lineSpacing.current;
      const deg = `${
        (Math.abs(Math.round(endMove / lineSpacing.current)) + 1) * rotation
      }deg`;

      setTransform(endMove, type, time, deg);

      setCurrIndex(Math.abs(Math.round(endMove / lineSpacing.current)) + 1);
    } else {
      let deg = 0;
      const currentDeg = (-updateMove / lineSpacing.current + 1) * rotation;

      // picker 滚动的最大角度
      const maxDeg = (data.length + 1) * rotation;
      const minDeg = 0;

      deg = Math.min(Math.max(currentDeg, minDeg), maxDeg);

      if (minDeg < deg && deg < maxDeg) {
        setTransform(updateMove, "", undefined, `${deg}deg`);
        setCurrIndex(
          Math.abs(Math.round(updateMove / lineSpacing.current)) + 1
        );
      }
    }
  };

  const setChooseValue = (move: number) => {
    const index = Math.round(-move / lineSpacing.current);
    onConfirm && onConfirm(data[index], keyIndex);
  };

  // 开始滚动
  const touchStart = event => {
    touch.start(event);
    setStartY(touch.deltaY);
    setStartTime(Date.now());
    transformY.current = scrollDistance;
  };

  const touchMove = event => {
    touch.move(event);
    if (touch.isVertical()) {
      moving.current = true;
      preventDefault(event, true);
    }
    const move = touch.deltaY - startY;
    setMove(move);
  };

  const touchEnd = () => {
    if (!moving.current) return;
    const move = touch.deltaY - startY;
    const moveTime = Date.now() - startTime;
    // 区分是否为惯性滚动
    if (moveTime <= INERTIA_TIME && Math.abs(move) > INERTIA_DISTANCE) {
      // 惯性滚动
      const distance = momentum(move, moveTime);
      setMove(distance, "end", +swipeDuration);
    } else {
      setMove(move, "end");
    }

    setTimeout(() => {
      touch.reset();
    }, 0);
  };

  // 惯性滚动 距离
  const momentum = (distance: number, duration: number) => {
    let nDistance = distance;
    // 惯性滚动的速度
    const speed = Math.abs(nDistance / duration);
    // 惯性滚动的距离
    nDistance = (speed / 0.003) * (nDistance < 0 ? -1 : 1);
    return nDistance;
  };

  const modifyStatus = (type?: boolean, val?: string | number) => {
    const value = val || defaultValue;
    let index = -1;
    if (value) {
      data.forEach((item, idx) => {
        if (typeof item === "object" ? item.value === value : item === value) {
          index = idx;
          return true;
        }
        return false;
      });
    } else {
      data.forEach((item, i) => {
        if (
          typeof item === "object"
            ? item.value === defaultValue
            : item === defaultValue
        ) {
          index = i;
        }
      });
    }

    setCurrIndex(index === -1 ? 1 : index + 1);
    const move = index === -1 ? 0 : index * lineSpacing.current;
    type && setChooseValue(-move);
    setMove(-move);
  };

  // 惯性滚动结束
  const stopMomentum = () => {
    moving.current = false;
    setTouchTime(0);
    setChooseValue(scrollDistance);
  };
  // 阻止默认事件
  const preventDefault = (
    event: React.TouchEvent<HTMLElement>,
    isStopPropagation?: boolean
  ) => {
    /* istanbul ignore else */
    if (typeof event.cancelable !== "boolean" || event.cancelable) {
      event.preventDefault();
    }

    if (isStopPropagation) {
      event.stopPropagation();
    }
  };

  const getReference = async () => {
    // const refe = await getRectByTaro(listbox?.current)
    // lineSpacing.current = refe.height ? refe.height : 36
    // lineSpacing.current =  36
    modifyStatus(true);
  };

  const touchRollerStyle = () => {
    return {
      transition: `transform ${touchTime}ms cubic-bezier(0.17, 0.89, 0.45, 1)`,
      transform: `rotate3d(1, 0, 0, ${touchDeg})`,
    };
  };
  const touchTileStyle = () => {
    return {
      transition: `transform ${touchTime}ms cubic-bezier(0.17, 0.89, 0.45, 1)`,
      transform: `translate3d(0, ${scrollDistance}px, 0)`,
    };
  };

  useEffect(() => {
    setScrollDistance(0);
    transformY.current = 0;
    modifyStatus(false);
    return () => {
      clearTimeout(timer);
    };
  }, [data]);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        getReference();
      }, 200);
    }
  }, [visible]);

  useImperativeHandle(ref, () => ({
    stopMomentum,
    moving: moving.current,
  }));

  return (
    <View
      className="xh-picker-list"
      ref={pickerSlotRef}
      onTouchStart={touchStart}
      onTouchMove={touchMove}
      onTouchEnd={touchEnd}
    >
      <View
        className="xh-picker-roller"
        ref={rollerRef}
        style={mode === "3D" ? touchRollerStyle() : touchTileStyle()}
        onTransitionEnd={stopMomentum}
      >
        {/* 3D 效果 */}
        {mode === "3D" &&
          data.map((item, index) => {
            return (
              <View
                className={`xh-picker-roller-item ${
                  isHidden(index + 1) && "nut-picker-roller-item-hidden"
                }`}
                style={{
                  transform: `rotate3d(1, 0, 0, ${
                    -rotation * (index + 1)
                  }deg) translate3d(0px, 0px, 104px)`,
                  height: `${lineSpacing.current}px`,
                  lineHeight: `${lineSpacing.current}px`,
                }}
                key={
                  typeof item === "string"
                    ? index
                    : item.value
                    ? item.value
                    : index
                }
              >
                <React.Fragment>
                  {typeof item === "object" ? item.text : item}
                </React.Fragment>
              </View>
            );
          })}
        {/* 平铺 */}
        {mode === "normal" &&
          data.map((item, index) => {
            return (
              <View
                className="xh-picker-roller-item-title"
                key={
                  typeof item === "object" ? item.value : item ? item : index
                }
                style={{
                  height: `${lineSpacing.current}px`,
                  lineHeight: `${lineSpacing.current}px`,
                }}
              >
                <React.Fragment>
                  {typeof item === "object" ? item.text : item}
                </React.Fragment>
              </View>
            );
          })}
      </View>

      <View className="xh-picker-mask" />
      <View className="xh-picker-indicator" ref={listbox} />
    </View>
  );
};
const PickerSlot = React.forwardRef<
  { stopMomentum: () => void; moving: boolean },
  Partial<PickerSlotProps>
>(InternalPickerSlot);
export default PickerSlot;
